import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, Upload, Zap, ToggleLeft, ToggleRight, FileText, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { formatCurrency } from '../services/api';

export default function BankReconciliation() {
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [autoReconEnabled, setAutoReconEnabled] = useState(false);
  const [reconRunning, setReconRunning] = useState(false);
  const [summary, setSummary] = useState({ total: 0, matched: 0, unmatched: 0 });

  useEffect(() => {
    load();
    api.get('/bank-reconciliation/settings').then(r => setAutoReconEnabled(r.data?.enabled || false)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api.get('/bank-reconciliation/statements').then(r => {
      const stmts = r.data?.statements || [];
      setStatements(stmts);
      if (stmts.length > 0) selectStatement(stmts[0]);
    }).catch(() => {}).finally(() => setLoading(false));
    api.get('/bank-reconciliation/ledger-entries').then(r => setLedgerEntries(r.data?.entries || [])).catch(() => {});
  };

  const selectStatement = (stmt) => {
    setSelectedStatement(stmt);
    const entries = stmt.entries || [];
    const matched = entries.filter(e => e.status === 'reconciled').length;
    setSummary({ total: entries.length, matched, unmatched: entries.length - matched });
  };

  const toggleAutoRecon = async () => {
    const newVal = !autoReconEnabled;
    setAutoReconEnabled(newVal);
    try {
      await api.put('/bank-reconciliation/settings', { enabled: newVal });
      toast.success(newVal ? 'Auto-reconcile enabled' : 'Auto-reconcile disabled');
    } catch { toast.error('Failed to save'); setAutoReconEnabled(!newVal); }
  };

  const handleAutoReconcile = async () => {
    if (!selectedStatement) { toast.error('Upload a statement first'); return; }
    setReconRunning(true);
    try {
      const r = await api.post('/bank-reconciliation/auto-reconcile', { statement_id: selectedStatement.statement_id });
      toast.success(`Matched ${r.data?.matched} entries out of ${r.data?.total_unmatched} unmatched`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-reconcile failed'); }
    finally { setReconRunning(false); }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const endpoint = type === 'pdf' ? '/api/bank-reconciliation/upload-pdf' : '/api/bank-reconciliation/upload';
      const r = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${r.data?.total || 0} entries from ${file.name}`);
      load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleManualReconcile = async (bankEntryId, ledgerEntryId) => {
    try {
      await api.post('/bank-reconciliation/reconcile', { bank_entry_id: bankEntryId, ledger_entry_id: ledgerEntryId });
      toast.success('Entry reconciled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reconciliation failed'); }
  };

  const entries = selectedStatement?.entries || [];
  const unmatchedEntries = entries.filter(e => e.status !== 'reconciled');
  const matchedEntries = entries.filter(e => e.status === 'reconciled');

  return (
    <div data-testid="bank-reconciliation-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Bank Reconciliation</h1>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[hsl(0,0%,55%)] mt-1">Match bank statements with ledger entries</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleAutoRecon} data-testid="auto-recon-toggle"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,18%)] hover:bg-[hsl(0,0%,96%)] dark:hover:bg-[hsl(0,0%,10%)] transition-all duration-200 text-[13px]">
            {autoReconEnabled
              ? <ToggleRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              : <ToggleLeft className="w-5 h-5 text-[hsl(0,0%,60%)]" />}
            <span className={autoReconEnabled ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-[hsl(0,0%,50%)]'}>Auto Reconcile</span>
          </button>
          <Button onClick={handleAutoReconcile} disabled={reconRunning || !selectedStatement}
            className="btn-accent flex items-center gap-2" data-testid="auto-reconcile-btn">
            <Zap className="w-4 h-4" /> {reconRunning ? 'Running...' : 'Auto Match'}
          </Button>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-5">
        <h3 className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em] mb-3">Import Bank Statement</h3>
        <div className="flex gap-4">
          <label className="flex-1 flex items-center justify-center gap-3 border-2 border-dashed border-[hsl(0,0%,88%)] dark:border-[hsl(0,0%,18%)] rounded-md p-6 cursor-pointer hover:border-[hsl(0,0%,65%)] hover:bg-[hsl(0,0%,98%)] dark:hover:bg-[hsl(0,0%,8%)] transition-all duration-200" data-testid="upload-csv-area">
            <Upload className="w-5 h-5 text-[hsl(0,0%,55%)]" />
            <div>
              <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Upload CSV</p>
              <p className="text-[10px] text-[hsl(0,0%,55%)]">Bank statement in CSV format</p>
            </div>
            <input type="file" accept=".csv" className="hidden" onChange={e => handleFileUpload(e, 'csv')} disabled={uploading} />
          </label>
          <label className="flex-1 flex items-center justify-center gap-3 border-2 border-dashed border-[hsl(0,0%,88%)] dark:border-[hsl(0,0%,18%)] rounded-md p-6 cursor-pointer hover:border-[hsl(0,0%,65%)] hover:bg-[hsl(0,0%,98%)] dark:hover:bg-[hsl(0,0%,8%)] transition-all duration-200" data-testid="upload-pdf-area">
            <FileText className="w-5 h-5 text-[hsl(var(--destructive))]" />
            <div>
              <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Upload PDF</p>
              <p className="text-[10px] text-[hsl(0,0%,55%)]">Bank statement in PDF format</p>
            </div>
            <input type="file" accept=".pdf" className="hidden" onChange={e => handleFileUpload(e, 'pdf')} disabled={uploading} />
          </label>
        </div>
        {uploading && <p className="text-[11px] text-[hsl(0,0%,55%)] mt-3 text-center">Uploading and parsing...</p>}
      </div>

      {/* Summary Cards */}
      {selectedStatement && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Entries', value: summary.total, icon: ArrowRightLeft },
            { label: 'Matched', value: summary.matched, icon: CheckCircle2, positive: true },
            { label: 'Unmatched', value: summary.unmatched, icon: AlertCircle, warning: true },
          ].map((item, i) => (
            <div key={i} className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-[hsl(0,0%,96%)] dark:bg-[hsl(0,0%,12%)] flex items-center justify-center">
                  <item.icon className={`w-4 h-4 ${item.positive ? 'text-emerald-600 dark:text-emerald-400' : item.warning ? 'text-amber-600 dark:text-amber-400' : 'text-[hsl(221,100%,50%)]'}`} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.12em]">{item.label}</p>
                  <p className={`text-xl font-bold font-mono ${item.positive ? 'text-emerald-700 dark:text-emerald-400' : item.warning ? 'text-amber-700 dark:text-amber-400' : 'text-[hsl(var(--foreground))]'}`}>{item.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unmatched Entries */}
      {unmatchedEntries.length > 0 && (
        <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)]">
            <h3 className="font-heading font-semibold text-sm tracking-tight text-[hsl(var(--foreground))]">Unmatched Entries ({unmatchedEntries.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="unmatched-table">
              <thead><tr>
                <th>Date</th><th>Description</th><th>Ref</th>
                <th className="text-right">Debit</th><th className="text-right">Credit</th>
                <th className="text-right">Balance</th><th className="text-center">Action</th>
              </tr></thead>
              <tbody>
                {unmatchedEntries.map((e, i) => (
                  <tr key={i}>
                    <td className="text-sm">{e.date}</td>
                    <td className="text-sm max-w-[220px] truncate">{e.description}</td>
                    <td className="text-xs font-mono text-[hsl(0,0%,55%)]">{e.reference || '-'}</td>
                    <td className="text-right font-mono text-sm">{e.debit > 0 ? `Rs.${formatCurrency(e.debit)}` : '-'}</td>
                    <td className="text-right font-mono text-sm">{e.credit > 0 ? `Rs.${formatCurrency(e.credit)}` : '-'}</td>
                    <td className="text-right font-mono text-sm">{e.balance ? `Rs.${formatCurrency(e.balance)}` : '-'}</td>
                    <td className="text-center">
                      <select onChange={ev => { if (ev.target.value) handleManualReconcile(e.bank_entry_id, ev.target.value); }}
                        className="text-xs border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,18%)] rounded-md px-2 py-1 bg-white dark:bg-[hsl(0,0%,7.8%)]" defaultValue="" data-testid={`match-select-${i}`}>
                        <option value="">Match with...</option>
                        {ledgerEntries.map(le => (
                          <option key={le.entry_id || le.voucher_number} value={le.entry_id || le.voucher_number}>
                            {le.date} | {le.voucher_number} | Rs.{le.debit > 0 ? le.debit : le.credit}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Matched Entries */}
      {matchedEntries.length > 0 && (
        <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)]">
            <h3 className="font-heading font-semibold text-sm tracking-tight text-emerald-700 dark:text-emerald-400">Reconciled Entries ({matchedEntries.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="matched-table">
              <thead><tr>
                <th>Date</th><th>Description</th><th className="text-right">Amount</th><th className="text-center">Status</th>
              </tr></thead>
              <tbody>
                {matchedEntries.map((e, i) => (
                  <tr key={i}>
                    <td className="text-sm">{e.date}</td>
                    <td className="text-sm">{e.description}</td>
                    <td className="text-right font-mono text-sm">Rs.{formatCurrency(e.debit || e.credit)}</td>
                    <td className="text-center"><span className="text-[9px] font-bold px-2 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900 uppercase tracking-[0.1em]">Reconciled</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedStatement && !loading && (
        <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md flex flex-col items-center justify-center py-20">
          <ArrowRightLeft className="w-12 h-12 text-[hsl(0,0%,85%)] dark:text-[hsl(0,0%,20%)] mb-4" strokeWidth={1} />
          <p className="text-[hsl(0,0%,55%)] text-sm">Upload a bank statement (CSV or PDF) to start reconciliation</p>
        </div>
      )}
    </div>
  );
}
